import { useCallback } from "react";

import {
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_ADDITIONAL_INFORMATION_VENTURE_CAPITAL_GROUP_TABLE_KEY as ADDITIONAL_INFORMATION,
} from "@frontend/common";

import {
  AdditionalInformationVentureCapital,
  VentureCapitalAdditionalInformationShellKeys,
} from "@/types/ventureCapital.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { PositionInfoProps } from "../VentureCapitalAdditionalInformationCreatorShell";
import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorShellReturn {
  handleKeyDownToClearAdditionalInformationShell: (
    event: KeyboardEvent,
  ) => void;
  handleClickToClearAdditionalInformationShell: () => void;
}

export const useHandleVentureCapitalAdditionalInformationCreatorShell = (
  positionPropsInfo: PositionInfoProps,
): UseHandleTableCreatorShellReturn => {
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const handleClearShell = useCallback(() => {
    const clearAdditionalInformationShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [SECTION_KEYS.ventureCapital]: {
          ...entryData[SECTION_KEYS.ventureCapital],
          [ADDITIONAL_INFORMATION]: {
            ...entryData[SECTION_KEYS.ventureCapital]?.[ADDITIONAL_INFORMATION],
            [info.shellKey as VentureCapitalAdditionalInformationShellKeys]: {
              ...entryData[SECTION_KEYS.ventureCapital]?.[
                ADDITIONAL_INFORMATION
              ]?.[
                info.shellKey as VentureCapitalAdditionalInformationShellKeys
              ],
              value: [],
            },
          } as AdditionalInformationVentureCapital,
        },
      };
    };

    if (info.groupTableKey === ADDITIONAL_INFORMATION) {
      setEntryData(clearAdditionalInformationShell());
      changeCurrentHistory({
        entryData: clearAdditionalInformationShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    entryData,
    info,
    positionPropsInfo,
    setEntryData,
  ]);

  const handleClickToClearAdditionalInformationShell = () => {
    if (focusStatus.type === "clear") {
      handleClearShell();
    }
  };

  const handleKeyDownToClearAdditionalInformationShell = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearShell();
      }
    },
    [focusStatus.type, handleClearShell],
  );

  return {
    handleClickToClearAdditionalInformationShell,
    handleKeyDownToClearAdditionalInformationShell,
  };
};
